import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Loader2, AlertTriangle, Bot, Minimize2, Maximize2 } from 'lucide-react';
import useChatSession from '../hooks/useChatSession';
import { ChatMessageList, ChatInput } from './chat/ChatParts';

function ChatTerms({ onAccept, isLoading, error }) {
  return (
    <div className="flex-1 p-6 flex flex-col justify-center" data-testid="chat-terms">
      <div className="bg-amber-50 border border-amber-200 p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-bold text-amber-900 text-sm mb-2">Aviso importante</h4>
            <p className="text-xs text-amber-800 leading-relaxed">
              Este asistente virtual utiliza <strong>Inteligencia Artificial</strong> para proporcionar
              información orientativa sobre prevención de riesgos laborales y reincorporaciones.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-3 text-xs text-slate-600 mb-6">
        {[
          <><strong>No es asesoramiento legal vinculante.</strong> Las respuestas son meramente informativas.</>,
          <><strong>No sustituye a un/a abogado/a.</strong> Para decisiones importantes, consulte con un profesional.</>,
          <><strong>Sus preguntas se almacenan</strong> de forma anónima para mejorar el servicio.</>,
          <>Consulte nuestra <a href="/legal/transparencia-ia" target="_blank" rel="noopener noreferrer" className="text-rose-600 underline">Transparencia IA (EU AI Act)</a>.</>
        ].map((text, idx) => (
          <p key={`term-${idx}`} className="flex items-start gap-2">
            <span className="text-rose-600 font-bold">•</span>
            <span>{text}</span>
          </p>
        ))}
      </div>
      <button
        onClick={onAccept}
        disabled={isLoading}
        className="w-full bg-rose-600 text-white py-3 font-bold hover:bg-rose-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="accept-chat-terms"
      >
        {isLoading ? <Loader2 className="animate-spin" size={18} /> : <>Entendido, comenzar chat <MessageCircle size={18} /></>}
      </button>
      {error && <p className="text-red-600 text-xs text-center mt-3">{error}</p>}
    </div>
  );
}

function ChatHeader({ isMinimized, onToggleMinimize, onClose }) {
  return (
    <div className="bg-slate-900 text-white p-4 flex items-center justify-between flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-rose-600 rounded-full flex items-center justify-center">
          <Bot size={20} />
        </div>
        <div>
          <h3 className="font-bold text-sm">Asistente PRL</h3>
          <p className="text-xs text-slate-400">Prevención de Riesgos Laborales</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onToggleMinimize} className="p-2 hover:bg-slate-800 rounded transition" aria-label={isMinimized ? 'Maximizar' : 'Minimizar'}>
          {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
        </button>
        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded transition" aria-label="Cerrar chat">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const inputRef = useRef(null);
  const chat = useChatSession();

  useEffect(() => {
    if (isOpen && chat.hasAcceptedTerms && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, chat.hasAcceptedTerms]);

  if (!isOpen) {
    return (
      <div className="fixed bottom-36 sm:bottom-6 right-4 sm:right-6 z-[10000]" data-testid="chat-widget-closed">
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 sm:w-16 sm:h-16 bg-rose-600 text-white rounded-full shadow-2xl hover:bg-rose-700 transition-all hover:scale-110 flex items-center justify-center"
          data-testid="chat-widget-button"
          aria-label="Abrir asistente virtual"
        >
          <MessageCircle size={26} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`fixed bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-96 bg-white shadow-2xl border border-slate-200 z-[10000] flex flex-col transition-all ${isMinimized ? 'h-14' : 'h-[80vh] sm:h-[600px] sm:max-h-[80vh]'}`}
      data-testid="chat-widget"
    >
      <ChatHeader isMinimized={isMinimized} onToggleMinimize={() => setIsMinimized(!isMinimized)} onClose={() => setIsOpen(false)} />

      {!isMinimized && (
        <>
          {!chat.hasAcceptedTerms ? (
            <ChatTerms onAccept={chat.acceptTerms} isLoading={chat.isLoading} error={chat.error} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                <ChatMessageList messages={chat.messages} isLoading={chat.isLoading} error={chat.error} messagesEndRef={chat.messagesEndRef} compact />
              </div>
              <ChatInput
                onSubmit={chat.sendMessage}
                inputMessage={chat.inputMessage}
                setInputMessage={chat.setInputMessage}
                isLoading={chat.isLoading}
                inputRef={inputRef}
                testIdPrefix="chat"
                compact
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
